import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  Key,
  Shield,
  ShieldCheck,
  ShieldAlert,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  Search
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ROLE_CONFIG = {
  master_admin: {
    label: 'Master Admin',
    color: 'bg-purple-600',
    icon: ShieldAlert,
    description: 'Full tilgang til alle funksjoner'
  },
  company_owner: {
    label: 'Bedriftseier',
    color: 'bg-blue-600',
    icon: ShieldCheck,
    description: 'Tilgang til bedriftsfunksjoner'
  },
  content_editor: {
    label: 'Innholdsredaktør',
    color: 'bg-green-600',
    icon: Shield,
    description: 'Kan redigere innhold og sider'
  },
  mechanic: {
    label: 'Mekaniker',
    color: 'bg-orange-600',
    icon: Shield,
    description: 'Forenklet grensesnitt - kun bookinger'
  }
};

const AdminUsers = () => {
  const { token, user: currentUser } = useAuth();
  const { toast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [tempPasswordModalOpen, setTempPasswordModalOpen] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'content_editor',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Feil ved henting av brukere:', error);
      toast({
        title: 'Feil',
        description: 'Kunne ikke hente brukere',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'content_editor',
      password: ''
    });
    setFormErrors({});
  };

  const validateForm = (isEdit = false) => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'E-post er påkrevd';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Ugyldig e-postadresse';
    }
    
    if (!formData.full_name) {
      errors.full_name = 'Navn er påkrevd';
    }
    
    if (!isEdit && !formData.password) {
      errors.password = 'Passord er påkrevd';
    } else if (!isEdit && formData.password.length < 12) {
      errors.password = 'Passord må være minst 12 tegn';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Bruker opprettet! ✅',
        description: `${formData.full_name} er nå lagt til`
      });
      
      setCreateModalOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke opprette bruker',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!validateForm(true)) return;
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      const updateData = {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role
      };
      
      // Only include password if it's been changed
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await axios.put(`${API}/users/${selectedUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Bruker oppdatert! ✅',
        description: `${formData.full_name} er oppdatert`
      });
      
      setEditModalOpen(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke oppdatere bruker',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Bruker slettet',
        description: `${selectedUser.full_name} er fjernet`
      });
      
      setDeleteModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke slette bruker',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/users/${selectedUser.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTempPassword(response.data.temporary_password);
      setResetPasswordModalOpen(false);
      setTempPasswordModalOpen(true);
      
      toast({
        title: 'Passord tilbakestilt! ✅',
        description: 'Midlertidig passord er generert'
      });
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke tilbakestille passord',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(
        `${API}/users/${user.id}`,
        { is_active: !user.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: user.is_active ? 'Bruker deaktivert' : 'Bruker aktivert',
        description: `${user.full_name} er nå ${user.is_active ? 'deaktivert' : 'aktivert'}`
      });
      
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke endre brukerstatus',
        variant: 'destructive'
      });
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch (err) {
      console.error('Kunne ikke kopiere:', err);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      password: ''
    });
    setEditModalOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Aldri';
    return new Date(dateString).toLocaleString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Brukeradministrasjon
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Søk etter bruker..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-600 text-white w-64"
                  data-testid="user-search-input"
                />
              </div>
              <Button
                onClick={() => {
                  resetForm();
                  setCreateModalOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Ny bruker
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Ingen brukere funnet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((user) => {
                const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.content_editor;
                const RoleIcon = roleConfig.icon;
                const isCurrentUser = user.id === currentUser?.id;
                
                return (
                  <div
                    key={user.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border transition-all ${
                      isCurrentUser 
                        ? 'bg-blue-900/20 border-blue-600/50' 
                        : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid={`user-item-${user.id}`}
                  >
                    <div className="flex items-center gap-4 mb-3 sm:mb-0">
                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full ${roleConfig.color} flex items-center justify-center`}>
                        <RoleIcon className="w-6 h-6 text-white" />
                      </div>
                      
                      {/* User Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-medium">{user.full_name}</h3>
                          {isCurrentUser && (
                            <Badge className="bg-blue-600 text-xs">Du</Badge>
                          )}
                          {user.must_change_password && (
                            <Badge className="bg-orange-600 text-xs">Må endre passord</Badge>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge className={`${roleConfig.color} text-xs`}>
                            {roleConfig.label}
                          </Badge>
                          <Badge className={user.is_active ? 'bg-green-600' : 'bg-red-600'}>
                            {user.is_active ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Aktiv</>
                            ) : (
                              <><XCircle className="w-3 h-3 mr-1" /> Deaktivert</>
                            )}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Last login & Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="text-right text-sm">
                        <p className="text-gray-500">Siste innlogging</p>
                        <p className="text-gray-300">{formatDate(user.last_login)}</p>
                      </div>
                      
                      {!isCurrentUser && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                            onClick={() => openEditModal(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-600 text-orange-400 hover:bg-orange-600/20"
                            onClick={() => {
                              setSelectedUser(user);
                              setResetPasswordModalOpen(true);
                            }}
                            data-testid={`reset-password-${user.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={user.is_active 
                              ? "border-yellow-600 text-yellow-400 hover:bg-yellow-600/20"
                              : "border-green-600 text-green-400 hover:bg-green-600/20"
                            }
                            onClick={() => handleToggleActive(user)}
                          >
                            {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-600 text-red-400 hover:bg-red-600/20"
                            onClick={() => {
                              setSelectedUser(user);
                              setDeleteModalOpen(true);
                            }}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-400" />
              Opprett ny bruker
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Fyll ut informasjon for den nye brukeren
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name" className="text-gray-300">Fullt navn</Label>
              <Input
                id="create-name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Ola Nordmann"
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="create-user-name"
              />
              {formErrors.full_name && (
                <p className="text-red-400 text-sm">{formErrors.full_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-email" className="text-gray-300">E-post</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ola@bedrift.no"
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="create-user-email"
              />
              {formErrors.email && (
                <p className="text-red-400 text-sm">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-role" className="text-gray-300">Rolle</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-gray-700">
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        <span>{config.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-gray-500 text-xs">
                {ROLE_CONFIG[formData.role]?.description}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-password" className="text-gray-300">Passord</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minst 12 tegn"
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="create-user-password"
              />
              {formErrors.password && (
                <p className="text-red-400 text-sm">{formErrors.password}</p>
              )}
              <p className="text-gray-500 text-xs">
                Må inneholde: stor bokstav, liten bokstav, tall, spesialtegn
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-create-user"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Opprett bruker
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-400" />
              Rediger bruker
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Oppdater brukerinformasjon
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-gray-300">Fullt navn</Label>
              <Input
                id="edit-name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="edit-user-name"
              />
              {formErrors.full_name && (
                <p className="text-red-400 text-sm">{formErrors.full_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-gray-300">E-post</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="edit-user-email"
              />
              {formErrors.email && (
                <p className="text-red-400 text-sm">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role" className="text-gray-300">Rolle</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-gray-700">
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        <span>{config.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-password" className="text-gray-300">
                Nytt passord <span className="text-gray-500">(valgfritt)</span>
              </Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="La stå tomt for å beholde"
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="edit-user-password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleEditUser}
              disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-edit-user"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Lagre endringer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Bekreft sletting
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette denne brukeren? Handlingen kan ikke angres.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
                <div className={`w-12 h-12 rounded-full ${ROLE_CONFIG[selectedUser.role]?.color || 'bg-gray-600'} flex items-center justify-center`}>
                  {React.createElement(ROLE_CONFIG[selectedUser.role]?.icon || Shield, { className: "w-6 h-6 text-white" })}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedUser.full_name}</p>
                  <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleDeleteUser}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Slett bruker
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Confirmation Modal */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-400" />
              Tilbakestill passord
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Dette vil generere et nytt midlertidig passord. Brukeren må endre det ved neste innlogging.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
                <div className={`w-12 h-12 rounded-full ${ROLE_CONFIG[selectedUser.role]?.color || 'bg-gray-600'} flex items-center justify-center`}>
                  {React.createElement(ROLE_CONFIG[selectedUser.role]?.icon || Shield, { className: "w-6 h-6 text-white" })}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedUser.full_name}</p>
                  <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetPasswordModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={actionLoading}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-reset-password"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Tilbakestill
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temporary Password Display Modal */}
      <Dialog open={tempPasswordModalOpen} onOpenChange={setTempPasswordModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Passord tilbakestilt
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Del dette midlertidige passordet med brukeren på en sikker måte
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="bg-gray-800 p-4 rounded-lg">
              <Label className="text-gray-400 text-sm">Midlertidig passord</Label>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-gray-900 px-4 py-3 rounded text-green-400 font-mono text-lg">
                  {tempPassword}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyPassword}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  {copiedPassword ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-orange-900/20 border border-orange-600/50 p-3 rounded-lg">
              <p className="text-orange-400 text-sm">
                <strong>⚠️ Viktig:</strong> Brukeren vil bli bedt om å endre passordet ved første innlogging.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => {
                setTempPasswordModalOpen(false);
                setTempPassword('');
                setSelectedUser(null);
              }}
              className="bg-gray-700 hover:bg-gray-600"
            >
              Lukk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
